/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import cx from 'classnames';
import Button from 'components/button/Button';
import { Carousel } from 'components/carousel/Carousel';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { BE_DEFINES } from 'constants/backend-defines.const';
import { I18nNamespace } from 'constants/i18n.const';
import {
  LAYOUT_ITEM_PER_PAGE,
  LAYOUT_ITEM_PER_PAGE_VERTICAL,
  QUANTITY_DEFAULT,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { includesId } from 'helpers/array.helper';
import { isEqualVal } from 'helpers/string.helper';
import { resourceUrl } from 'helpers/url.helper';
import { usePageTimer } from 'hooks/usePageTimer';
import { map, range, size, slice } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import {
  filterListLayoutByQuantity,
  getListQuantityOfPerSheet,
  sortLayoutsByCodePrefix,
} from './layout-helper';
import './select-layout.css';
import { useLayoutActions } from './useLayoutActions';

function SelectLayout() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const dispatch = useAppDispatch();
  const { audio } = useSoundContext();
  const { listLayoutPhotos } = useBoothAppContext();

  const { quantityPicOfPerSheet = QUANTITY_DEFAULT, frameId } = useAppSelector(
    (state) => state.selfeApp,
  );
  const { isThemeBeforePayment, boothInfo, isVerticalScreen, isEventBooth } =
    useAppSelector((s) => s.booth);
  const { handleSelectLayout } = useLayoutActions();

  const { second } = usePageTimer({
    forwardFunc: () => handleSelectLayout(undefined, undefined)(),
    isBeforeTransaction: true,
  });

  // danh sách layout theo frameId và số ảnh trên 1 sheet
  const listLayoutByQuantity = useMemo(() => {
    const filteredLayouts = isEventBooth
      ? listLayoutPhotos?.data
      : filterListLayoutByQuantity(
          listLayoutPhotos?.data,
          frameId,
          quantityPicOfPerSheet,
        );

    // Sắp xếp theo thứ tự code: 2x6 -> 4x6 -> 6x20
    return sortLayoutsByCodePrefix(filteredLayouts || []);
  }, [isEventBooth, frameId, listLayoutPhotos?.data, quantityPicOfPerSheet]);

  // Số layout item trên 1 page
  const layoutPerPage = useMemo(() => {
    const verticalItemPerPage = LAYOUT_ITEM_PER_PAGE_VERTICAL;

    return isVerticalScreen ? verticalItemPerPage : LAYOUT_ITEM_PER_PAGE;
  }, [isVerticalScreen]);

  // danh sách của số ảnh layout theo frameId => [1, 3, 4, 6, 8]
  const listQuantityOfPerSheet = useMemo(
    () => getListQuantityOfPerSheet(listLayoutPhotos?.data, frameId),
    [frameId, listLayoutPhotos?.data],
  );

  const handleSelectQuantity = (amount: number) => () => {
    audio?.playClickToSelect?.();
    dispatch(setSelfeAppStateAction({ quantityPicOfPerSheet: amount }));
  };

  // select mặc định số lượng ảnh trên 1 layout
  React.useEffect(() => {
    if (
      listQuantityOfPerSheet?.[0] &&
      !includesId(listQuantityOfPerSheet, quantityPicOfPerSheet)
    ) {
      dispatch(
        setSelfeAppStateAction({
          quantityPicOfPerSheet: listQuantityOfPerSheet?.[0],
        }),
      );
    }
  }, [dispatch, listQuantityOfPerSheet, quantityPicOfPerSheet]);

  return (
    <>
      <div className="select-layout">
        <TimerText second={second} />
        <div className="select-layout__quantity-group">
          {map(listQuantityOfPerSheet, (quantity) => {
            const isActive = !!isEqualVal(
              quantityPicOfPerSheet || QUANTITY_DEFAULT,
              quantity,
            );
            return (
              <Button
                key={quantity}
                color="primary"
                onClick={handleSelectQuantity(quantity)}
                className={cx('select-layout__quantity-button', {
                  active: isActive,
                })}
                noBorder={!isActive}
              >
                {quantity < 2
                  ? `${quantity} ${t('common:picture')}`
                  : `${quantity} ${t('common:pictures')}`}
              </Button>
            );
          })}
        </div>
        {boothInfo?.Type === BE_DEFINES.EVENT_BOOTH ? (
          <Carousel
            className="layout-carousel"
            OuterProps={{ className: 'layout-page__layout-carousel' }}
            slides={map(
              range(
                1,
                Math.ceil((size(listLayoutByQuantity) || 0) / layoutPerPage) +
                  1,
              ),
            )}
            hiddenBtn={size(listLayoutByQuantity) <= layoutPerPage}
            RenderSlide={(_, ind) => (
              <div className="layout-group add-layout__grid">
                {map(
                  slice(
                    listLayoutByQuantity,
                    ind * layoutPerPage,
                    (ind + 1) * layoutPerPage,
                  ),
                  (item) => {
                    const layoutImage = isThemeBeforePayment
                      ? item?.image
                      : item?.imageTemplate;
                    return (
                      <img
                        className="transition-img"
                        key={item?.id}
                        src={resourceUrl(layoutImage)}
                        onClick={handleSelectLayout(
                          item?.id,
                          item?.themeDetailId,
                          item,
                        )}
                        alt="example-img"
                      />
                    );
                  },
                )}
              </div>
            )}
          />
        ) : (
          <div className="select-layout__img-examples">
            {size(listLayoutByQuantity) ? (
              map(listLayoutByQuantity, (item) => {
                const layoutImage = isThemeBeforePayment
                  ? item?.image
                  : item?.imageTemplate;

                return (
                  <img
                    className="transition-img"
                    key={item?.id}
                    src={resourceUrl(layoutImage)}
                    onClick={handleSelectLayout(
                      item?.id,
                      item?.themeDetailId,
                      item,
                    )}
                    alt="example-img"
                  />
                );
              })
            ) : (
              <Typography variant={TYPOGRAPHY_VARIANTS.BODY}>
                {t('common:noData')}
              </Typography>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default SelectLayout;
